import { useState, useEffect } from "react";
import { useModalStore } from "../../../stores/modalStore.js";
import { useAeThex } from "../../AeThexProvider";
import { useFriendStore } from "../../../stores/friendStore.js";

const ROLE_META = {
  owner:     { label: 'Owner',     color: '#f59e0b', bg: 'rgba(245,158,11,0.15)',  border: 'rgba(245,158,11,0.35)' },
  admin:     { label: 'Admin',     color: '#f43f5e', bg: 'rgba(244,63,94,0.15)',   border: 'rgba(244,63,94,0.35)'  },
  moderator: { label: 'Moderator', color: '#10b981', bg: 'rgba(16,185,129,0.15)',  border: 'rgba(16,185,129,0.35)' },
  member:    { label: 'Member',    color: '#6b7280', bg: 'rgba(107,114,128,0.15)', border: 'rgba(107,114,128,0.35)' },
};

const DIVISION_META = {
  Founder:     { label: 'Founder',     bg: 'linear-gradient(90deg,#cc0000,#0044cc,#cc7700)', color: '#fff',    border: 'none' },
  Foundation:  { label: 'Foundation',  bg: 'rgba(255,0,0,0.12)',   color: '#ff5555', border: '1px solid rgba(255,0,0,0.35)'   },
  Labs:        { label: 'Labs',        bg: 'rgba(255,165,0,0.12)', color: '#ffaa33', border: '1px solid rgba(255,165,0,0.35)' },
  Corporation: { label: 'Corporation', bg: 'rgba(0,102,255,0.12)', color: '#4488ff', border: '1px solid rgba(0,102,255,0.35)' },
};

const STATUS_DOT = {
  online:  { color: '#3ba55c', label: 'Online' },
  idle:    { color: '#faa61a', label: 'Idle' },
  dnd:     { color: '#ed4245', label: 'Do Not Disturb' },
  offline: { color: '#747f8d', label: 'Offline' },
};

const BANNER_GRADIENT = {
  owner:     'linear-gradient(135deg,#92400e,#78350f)',
  admin:     'linear-gradient(135deg,#881337,#4c0519)',
  moderator: 'linear-gradient(135deg,#064e3b,#022c22)',
  member:    'linear-gradient(135deg,#1e1b4b,#0f0a2e)',
};

const ACTIVITY_VERB = { playing: 'Playing', listening: 'Listening to', watching: 'Watching', competing: 'Competing in' };

export default function UserProfileModal() {
  const { isOpen, type, data, onClose } = useModalStore();
  const { user } = useAeThex();
  const { friends, sendRequest } = useFriendStore();
  const [addMsg, setAddMsg] = useState(null);
  const [richPresence, setRichPresence] = useState(null);

  if (!isOpen || type !== "userProfile") return null;

  const member = data?.member || user || {};
  const displayName = member.display_name || member.name || member.username || 'Unknown';
  const initial = displayName[0]?.toUpperCase() || '?';
  const role = (member.role || 'member').toLowerCase();
  const roleMeta = ROLE_META[role] || ROLE_META.member;
  const badge = member.badge || member.division;
  const divMeta = badge ? (DIVISION_META[badge] || null) : null;
  const status = member.status || 'offline';
  const statusMeta = STATUS_DOT[status] || STATUS_DOT.offline;
  const isOwnProfile = !data?.member || member.id === user?.id;
  const bannerBg = member.banner_url ? `url(${member.banner_url})` : (BANNER_GRADIENT[role] || BANNER_GRADIENT.member);
  const isFriend = friends.some(f => f.id === member.id);

  const handleAddFriend = async () => {
    const d = await sendRequest(member.id || member.username);
    setAddMsg(d.accepted ? 'Already friends!' : d.success ? 'Request sent!' : (d.error || 'Failed'));
    setTimeout(() => setAddMsg(null), 3000);
  };

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
      onClick={onClose}
    >
      <div
        style={{ background: '#1a1a2e', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, width: 340, overflow: 'hidden', boxShadow: '0 24px 64px rgba(0,0,0,0.6)' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Banner */}
        <div style={{ height: 90, background: bannerBg, backgroundSize: 'cover', backgroundPosition: 'center', position: 'relative', flexShrink: 0 }}>
          <button
            onClick={onClose}
            style={{ position: 'absolute', top: 10, right: 10, background: 'rgba(0,0,0,0.45)', border: 'none', color: 'rgba(255,255,255,0.6)', borderRadius: '50%', width: 26, height: 26, cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}
          >✕</button>
        </div>

        {/* Avatar row */}
        <div style={{ padding: '0 16px', position: 'relative' }}>
          <div style={{ marginTop: -38, marginBottom: 8, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
            <div style={{ position: 'relative', flexShrink: 0 }}>
              {member.avatar_url ? (
                <img src={member.avatar_url} alt={displayName} style={{ width: 76, height: 76, borderRadius: '50%', border: '4px solid #1a1a2e', objectFit: 'cover', display: 'block' }} />
              ) : (
                <div style={{ width: 76, height: 76, borderRadius: '50%', border: '4px solid #1a1a2e', background: bannerBg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 30, fontWeight: 800, color: '#fff', boxSizing: 'border-box' }}>
                  {member.avatar || initial}
                </div>
              )}
              {/* Status dot */}
              <span style={{ position: 'absolute', bottom: 3, right: 3, width: 14, height: 14, borderRadius: '50%', background: statusMeta.color, border: '2.5px solid #1a1a2e', display: 'block' }} />
            </div>

            {/* Quick action buttons */}
            {!isOwnProfile && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingBottom: 4, alignItems: 'flex-end' }}>
                <button style={{ padding: '6px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.75)' }}>
                  💬 Message
                </button>
                {!isFriend && (
                  <button onClick={handleAddFriend} style={{ padding: '6px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', background: 'rgba(99,102,241,0.2)', border: '1px solid rgba(99,102,241,0.4)', color: '#a5b4fc' }}>
                    + Friend
                  </button>
                )}
                {addMsg && <div style={{ fontSize: 11, color: '#34d399' }}>{addMsg}</div>}
              </div>
            )}
          </div>

          {/* Name */}
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#fff', lineHeight: 1.2 }}>{displayName}</div>
            {member.username && member.username !== displayName && (
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>@{member.username}</div>
            )}
          </div>

          {/* Badges row */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
            {/* Division / affiliation badge */}
            {divMeta && (
              <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 4, background: divMeta.bg, color: divMeta.color, border: divMeta.border, letterSpacing: '0.02em' }}>
                {divMeta.label}
              </span>
            )}
            {/* Role badge — only show if not generic member */}
            {role !== 'member' && (
              <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 4, background: roleMeta.bg, color: roleMeta.color, border: `1px solid ${roleMeta.border}`, letterSpacing: '0.02em' }}>
                {roleMeta.label}
              </span>
            )}
          </div>

          {/* Divider */}
          <div style={{ height: 1, background: 'rgba(255,255,255,0.07)', marginBottom: 14 }} />

          {/* Status */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>Status</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: statusMeta.color, flexShrink: 0, display: 'inline-block' }} />
              <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.65)' }}>{statusMeta.label}</span>
            </div>
            {member.custom_status && (
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.38)', fontStyle: 'italic', marginTop: 4 }}>"{member.custom_status}"</div>
            )}
          </div>

          {/* Bio */}
          {member.bio && (
            <>
              <div style={{ height: 1, background: 'rgba(255,255,255,0.07)', marginBottom: 14 }} />
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>About Me</div>
                <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.65)', lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>{member.bio}</div>
              </div>
            </>
          )}

          {/* Activity / Rich Presence */}
          {(member.activity || member.activityType) && (
            <>
              <div style={{ height: 1, background: 'rgba(255,255,255,0.07)', marginBottom: 14 }} />
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>Activity</div>
                <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.65)' }}>
                  <span style={{ color: 'rgba(255,255,255,0.38)', fontSize: 11, marginRight: 4 }}>
                    {ACTIVITY_VERB[member.activityType] || 'Playing'}
                  </span>
                  {member.activity}
                </div>
                {member.statusText && (
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.38)', fontStyle: 'italic', marginTop: 3 }}>{member.statusText}</div>
                )}
              </div>
            </>
          )}

          {/* Division info row */}
          {member.division && (
            <>
              <div style={{ height: 1, background: 'rgba(255,255,255,0.07)', marginBottom: 14 }} />
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>Division</div>
                <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.65)' }}>AeThex {member.division}</div>
              </div>
            </>
          )}

          {/* Bottom close button for own profile */}
          {isOwnProfile && (
            <button
              onClick={onClose}
              style={{ width: '100%', marginBottom: 16, padding: '8px', borderRadius: 8, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', fontSize: 13 }}
            >
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
